<template>
  <div class="canvas-tasks">
    <p v-if="enabledSections.length === 0" class="no-sections">
      No sections enabled. Select the widget to enable some.
    </p>
    <!-- A single enabled section hides its retractable header (the widget
         itself is the section); several sections each get one. Like the
         Agenda, empty sections hide entirely — except the day's, which
         always shows (it hosts the "Add task…" input). Each section is a
         drop target rescheduling the dropped task or allocation (Overdue
         is display-only; `acceptsSectionDrop` refuses it). -->
    <Drop
      v-for="section in visibleSections"
      v-else
      :key="section"
      :accepts-type="['allocation', 'task']"
      :accepts-data="acceptsSectionDrop(section)"
      class="list"
      :class="{ dragOver: hoverState === section }"
      @dragover="
        (e: DraggedAllocation | DraggedTask) => onSectionDragOver(e, section)
      "
      @dragleave="() => (hoverState = null)"
      @drop="(e: DraggedAllocation | DraggedTask) => onSectionDrop(e, section)"
      @dragend="() => (hoverState = null)"
    >
      <CollapsibleTaskList
        :date="date"
        :is-today="isTodayDate"
        :items="sectionItems(section)"
        :title="sectionTitle(section)"
        :show-header="enabledSections.length > 1"
        :is-overdue="section === 'overdue'"
        :can-add-task="sectionCanAddTask(section)"
        :hide-scheduled-date="section === 'day'"
        :pagination="sectionPagination(section)"
        :list-key="sectionListKey(section)"
        :is-collapsed="isSectionCollapsed(section)"
        @update:is-collapsed="setSectionCollapsed(section, $event)"
      />
    </Drop>
  </div>
</template>

<script lang="ts">
import { addWeeks, format, isAfter, isToday, startOfDay } from "date-fns";
import { computed, defineComponent, ref, toRefs, type PropType } from "vue";
import { Drop } from "vue-easy-dnd";

import controller from "@ctrl/controller";
import type { GeneralizedTaskId } from "@ctrl/controller-rpc";

import assert from "@/assert";
import CollapsibleTaskList from "@/components/agenda/CollapsibleTaskList.vue";
import {
  acceptsWeekPlannable,
  dropPlannableOnSection,
  type DroppableTaskSection,
} from "@/components/agenda/dnd";
import * as inject from "@/components/inject";
import {
  dayKey,
  personalSectionKey,
  taskListKeys,
  weekKey,
} from "@/components/tasks/ordering";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { bindAsync, useReleaser } from "@/ref";
import type { DraggedAllocation, DraggedTask } from "@/types";

import { CANVAS_TASKS_SECTIONS, type CanvasTasksSection } from "./body";

type SectionState = "collapsed" | "expanded";

const { weekOfDate } = controller;

/**
 * The canvas Tasks content widget: the Agenda's task groupings for the
 * canvas's selected date, restricted to the sections enabled in the widget's
 * configuration. Several sections render as retractable titled lists (their
 * collapsed state persists per widget in the browser store); a single one
 * renders as a bare list without a header.
 */
export default defineComponent({
  name: "CanvasTasksWidget",
  components: {
    CollapsibleTaskList,
    Drop,
  },
  props: {
    /** Enabled sections (see `CanvasTasksWidget` in `body.ts`). */
    sections: {
      type: Array as PropType<CanvasTasksSection[]>,
      required: true,
    },
    /** The canvas's selected date (the top bar's date picker). */
    date: { type: Date, required: true },
    /** Per-widget browser-store prefix (collapsed states live under it). */
    storagePrefix: { type: String, required: true },
  },
  async setup(props) {
    const { date } = toRefs(props);
    const controller = inject.controller();
    const clock = inject.clock();
    const profile = inject.profile();
    const releaser = useReleaser();

    // Collapsed states persist per widget and section, so two Tasks widgets
    // on the same canvas retract independently.
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const sectionState = (section: CanvasTasksSection) =>
      releaser.add(
        syncState<SectionState>(
          `${props.storagePrefix}/sections/${section}`,
          "expanded"
        )
      );
    const collapsedStates = {
      day: sectionState("day"),
      week: sectionState("week"),
      overdue: sectionState("overdue"),
      upcoming: sectionState("upcoming"),
      unplanned: sectionState("unplanned"),
      completed: sectionState("completed"),
      discarded: sectionState("discarded"),
    };

    // Canonical order (and deduplication), whatever the stored order.
    const enabledSections = computed(() =>
      CANVAS_TASKS_SECTIONS.filter((section) =>
        props.sections.includes(section)
      )
    );
    const agendaTasks = await releaser.add(
      bindAsync(date, async (d) => await controller.agendaTasks(d))
    );
    // Same HACK as the Agenda's Tasks.vue: agendaTasks has no week group,
    // so the Week section needs the planner's calendarTasks batch just for
    // its `postponed` ids — knowingly unoptimized until the backend exposes
    // a week-only RPC or adds it to agendaTasks().
    const calendarTasks = await releaser.add(
      bindAsync(date, async (d) => await controller.calendarTasks(d))
    );

    const isTodayDate = computed(() => {
      // The assert allows for today to be recomputed when we change day.
      assert(clock.value);
      return isToday(date.value);
    });
    const isInPast = computed(() =>
      isAfter(startOfDay(clock.value), startOfDay(date.value))
    );
    const week = computed(() => weekOfDate(date.value));

    const sectionItems = (section: CanvasTasksSection): GeneralizedTaskId[] => {
      const tasks = agendaTasks.value;
      switch (section) {
        case "day":
          return tasks?.current ?? [];
        case "week":
          return calendarTasks.value?.postponed ?? [];
        case "overdue":
          return tasks?.overdue ?? [];
        case "upcoming":
          return tasks?.upcoming ?? [];
        case "unplanned":
          return tasks?.unplanned ?? [];
        case "completed":
          return tasks?.completed ?? [];
        case "discarded":
          return tasks?.ignored ?? [];
      }
    };

    // Like the Agenda, an empty section hides entirely rather than showing
    // an empty state — except the day's, which always shows. The header
    // (retractable) logic stays on `enabledSections`: what the user toggled,
    // not what currently has tasks.
    const visibleSections = computed(() =>
      enabledSections.value.filter(
        (section) => section === "day" || sectionItems(section).length > 0
      )
    );

    const sectionTitle = (section: CanvasTasksSection): string => {
      switch (section) {
        case "day":
          return isTodayDate.value ? "Today" : format(date.value, "EEEE");
        case "week":
          return `Week ${week.value.n}`;
        case "overdue":
          return "Overdue";
        case "upcoming":
          return "Upcoming";
        case "unplanned":
          return "Unplanned";
        case "completed":
          return "Completed";
        case "discarded":
          return "Discarded";
      }
    };

    // Only the day list takes new tasks (they schedule to that date), and
    // only when the date is not in the past — same as the Agenda.
    const sectionCanAddTask = (section: CanvasTasksSection): boolean =>
      section === "day" && !isInPast.value;

    const sectionPagination = (
      section: CanvasTasksSection
    ): number | undefined => (section === "unplanned" ? 20 : undefined);

    // Shared ranking keys — the same lists as the Agenda and the Planner
    // sidebar, so reordering here and there stays consistent. Overdue and
    // Upcoming keep the controller's order (no key).
    const sectionListKey = (
      section: CanvasTasksSection
    ): string | undefined => {
      switch (section) {
        case "day":
          return personalSectionKey(profile.value, dayKey(date.value));
        case "week":
          return personalSectionKey(profile.value, weekKey(week.value));
        case "unplanned":
          return personalSectionKey(profile.value, taskListKeys.unplanned);
        default:
          return undefined;
      }
    };

    const isSectionCollapsed = (section: CanvasTasksSection): boolean =>
      collapsedStates[section].value === "collapsed";
    const setSectionCollapsed = (
      section: CanvasTasksSection,
      value: boolean
    ) => {
      collapsedStates[section].value = value ? "collapsed" : "expanded";
    };

    // Dropping a task or allocation on a section reschedules it, exactly as
    // in the Agenda's task pane (`dropPlannableOnSection`). Overdue is
    // display-only, and the widget's "day" section is the Agenda's "today".
    const droppableSection = (
      section: CanvasTasksSection
    ): DroppableTaskSection | null =>
      section === "overdue" ? null : section === "day" ? "today" : section;

    // `accepts-data` of a section's drop zone: refuses Overdue, and refuses
    // week-scheduling sections for bound tasks (see `acceptsWeekPlannable`).
    const acceptsSectionDrop =
      (section: CanvasTasksSection) =>
      (data: {
        task?: { id?: string; binding?: unknown; isBound?: boolean };
      }): boolean => {
        const target = droppableSection(section);
        if (!target) return false;
        if (target === "week" || target === "upcoming") {
          return acceptsWeekPlannable(data);
        }
        return true;
      };

    const hoverState = ref<CanvasTasksSection | null>(null);

    const onSectionDragOver = (
      e: DraggedAllocation | DraggedTask,
      section: CanvasTasksSection
    ) => {
      // `accepts-data` refuses the drop itself, but vue-easy-dnd still fires
      // `dragover`: don't highlight a section that will not take the payload.
      if (!acceptsSectionDrop(section)(e.data)) return;
      hoverState.value = section;
    };

    const onSectionDrop = (
      e: DraggedAllocation | DraggedTask,
      section: CanvasTasksSection
    ) => {
      hoverState.value = null;
      const target = droppableSection(section);
      if (!target || !e.success) return;
      // Handle reorder is Ordering-only; same-section row drop is a no-op.
      const items: readonly string[] = sectionItems(section);
      if (e.type === "task" && items.includes(e.data.task.id)) return;
      dropPlannableOnSection(controller, e, target, {
        date: date.value,
        week: week.value,
        nextWeek: weekOfDate(addWeeks(new Date(), 1)),
      });
    };

    return {
      enabledSections,
      visibleSections,
      isTodayDate,
      sectionItems,
      sectionTitle,
      sectionCanAddTask,
      sectionPagination,
      sectionListKey,
      isSectionCollapsed,
      setSectionCollapsed,
      hoverState,
      acceptsSectionDrop,
      onSectionDragOver,
      onSectionDrop,
    };
  },
});
</script>

<style scoped lang="scss">
.canvas-tasks {
  width: 100%;
  padding: 8px;
}

// Section drop zones highlight like the Agenda's task pane.
.list {
  border-radius: $border-radius-l;
  border: 1px solid transparent;
  @include transition(border 0.2s ease-out);

  &.dragOver {
    @include themed() {
      border: 0.5px dashed t($secondary-50);
    }
  }
}

.no-sections {
  @extend %font-caption-regular;
  margin: 8px;
  @include themed() {
    color: t($grey-60);
  }
}
</style>
